/**
 * A plataforma em números: tudo contado do banco (`/api/numeros`).
 *
 * A premiação é o número que abre, grande como placar; os outros formam a
 * linha de baixo. Os valores contam do zero quando a seção entra na tela, uma
 * vez só. Sem dado ainda, a seção não aparece -- zero de propaganda não serve.
 *
 * `naHero` é a versão compacta que fecha a hero da home: premiação à esquerda e
 * os outros cinco numa linha ao lado, sem fundo próprio.
 */

import { useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { gsap, ScrollTrigger, useGSAP, countUp } from '@/lib/motion';

interface Numeros {
  edicoesRealizadas: number;
  premiacaoCents: number;
  partidas: number;
  gols: number;
  clubes: number;
  atletas: number;
}

const inteiro = (v: number) => Math.round(v).toLocaleString('pt-BR');
const reais = (v: number) => `R$ ${Math.round(v).toLocaleString('pt-BR')}`;

export function NumerosDaPlataforma({ naHero = false }: { naHero?: boolean }) {
  const { data } = useQuery({ queryKey: ['numeros'], queryFn: () => api<Numeros>('/numeros'), staleTime: 5 * 60_000 });
  const raiz = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      if (!data || !raiz.current) return;
      const alvos = raiz.current.querySelectorAll<HTMLElement>('[data-conta]');
      ScrollTrigger.create({
        trigger: raiz.current,
        // Na hero a faixa já nasce visível: conta assim que aparece.
        start: naHero ? 'top bottom' : 'top 75%',
        once: true,
        onEnter: () => {
          alvos.forEach((el) => {
            const valor = Number(el.dataset.conta);
            countUp(el, valor, { duration: 1.6, format: el.dataset.formato === 'reais' ? reais : inteiro });
          });
          gsap.from(raiz.current!.querySelectorAll('[data-numero]'), { y: 24, autoAlpha: 0, duration: 0.9, stagger: 0.07 });
        },
      });
    },
    { dependencies: [data], scope: raiz },
  );

  if (!data || data.edicoesRealizadas === 0) return null;

  const linha = [
    { valor: data.edicoesRealizadas, rotulo: data.edicoesRealizadas === 1 ? 'edição realizada' : 'edições realizadas' },
    { valor: data.partidas, rotulo: 'partidas disputadas' },
    { valor: data.gols, rotulo: 'gols marcados' },
    { valor: data.clubes, rotulo: 'clubes cadastrados' },
    { valor: data.atletas, rotulo: 'atletas inscritos' },
  ];

  if (naHero) {
    return (
      <div ref={raiz} className="grid gap-8 lg:grid-cols-[auto_minmax(0,1fr)] lg:items-end lg:gap-12">
        <div data-numero className="flex flex-col gap-1">
          <p className="text-[0.6875rem] font-semibold tracking-wider text-muted uppercase">Em premiações nas edições encerradas</p>
          <p
            className="ucc-display tabular text-[clamp(3rem,7vw,4.5rem)] leading-none text-gold-ink"
            data-conta={data.premiacaoCents / 100}
            data-formato="reais"
          >
            {reais(data.premiacaoCents / 100)}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-3 lg:grid-cols-5">
          {linha.map((n) => (
            <div key={n.rotulo} data-numero className="flex flex-col gap-0.5 border-l border-line-strong pl-4">
              <dd className="ucc-display tabular text-4xl text-paper xl:text-5xl" data-conta={n.valor}>
                {inteiro(n.valor)}
              </dd>
              <dt className="order-last text-xs text-muted">{n.rotulo}</dt>
            </div>
          ))}
        </dl>
      </div>
    );
  }

  return (
    <section ref={raiz} className="relative z-10 overflow-hidden border-t border-line bg-void py-24" aria-labelledby="numeros-titulo">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(50rem_22rem_at_50%_0%,rgb(248_212_40/0.10),transparent_70%)]"
      />
      <div className="relative mx-auto max-w-ucc px-6">
        <h2 id="numeros-titulo" className="sr-only">A plataforma em números</h2>
        <div data-numero className="flex flex-col items-start gap-2 border-b border-line pb-12 md:items-center md:text-center">
          <p className="text-sm font-semibold tracking-wider text-muted uppercase">Em premiações nas edições encerradas</p>
          <p
            className="ucc-display tabular text-[clamp(4rem,14vw,6rem)] leading-none text-gold-ink"
            data-conta={data.premiacaoCents / 100}
            data-formato="reais"
          >
            {reais(data.premiacaoCents / 100)}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-10 pt-12 sm:grid-cols-3 lg:grid-cols-5">
          {linha.map((n) => (
            <div key={n.rotulo} data-numero className="flex flex-col gap-1 border-l border-line-strong pl-5">
              <dd className="ucc-display tabular text-5xl text-paper md:text-6xl" data-conta={n.valor}>
                {inteiro(n.valor)}
              </dd>
              <dt className="order-last text-sm text-muted">{n.rotulo}</dt>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
